import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getGraphQLClient, GET_BOOK_GITHUB_SYNC_STATE } from '../../lib/graphql';

type SyncState = {
  syncConfigured: boolean;
  remoteCommitSha: string | null;
  lastSyncedCommitSha: string | null;
  remoteAhead: boolean;
  incomingCount: number;
  conflictCount: number;
  reorderCount: number;
};

const POLL_INTERVAL_MS = 90_000;

type Props = {
  bookId: string;
};

export default function GithubSyncBanner({ bookId }: Props) {
  const { t } = useTranslation();
  const [state, setState] = useState<SyncState | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchState = async () => {
      try {
        const data = await getGraphQLClient().request<{
          bookGithubSyncState: SyncState | null;
        }>(GET_BOOK_GITHUB_SYNC_STATE, { bookId });
        if (!cancelled) setState(data.bookGithubSyncState);
      } catch {
        // Silent — banner is best-effort; the sync page surfaces real errors.
      }
    };

    fetchState();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchState();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [bookId]);

  if (!state || !state.syncConfigured || !state.remoteAhead) return null;

  const totalChanges = state.incomingCount + state.conflictCount + state.reorderCount;
  if (totalChanges === 0) return null;

  return (
    <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-4 py-3 flex items-baseline justify-between gap-3 text-sm">
      <div className="text-amber-900 dark:text-amber-200">
        {t(
          'githubSync.banner.remoteHasChanges',
          'Remote has changes: {{incoming}} incoming, {{conflicts}} conflicts, {{reorders}} reorders.',
          {
            incoming: state.incomingCount,
            conflicts: state.conflictCount,
            reorders: state.reorderCount,
          }
        )}
      </div>
      <Link
        to={`/books/${bookId}/sync`}
        className="font-medium text-amber-900 dark:text-amber-200 underline whitespace-nowrap"
      >
        {t('githubSync.banner.review', 'Review →')}
      </Link>
    </div>
  );
}
